import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';

const AuthContext = createContext(null);

const TOKEN_KEY = 'maximuz_token';
// Impersonation sessions live in sessionStorage — tab-scoped, unlike localStorage
// which is shared across every tab of the origin. This lets a superadmin open
// "Log in as X" in a new tab while their own tab's real session stays untouched.
const IMPERSONATION_KEY = 'maximuz_impersonation_token';

const getStoredToken = () => sessionStorage.getItem(IMPERSONATION_KEY) || localStorage.getItem(TOKEN_KEY);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // A tab opened via "Log in as X" carries its token in the URL hash rather
    // than shared storage. Pick it up once, stash it tab-locally, then scrub
    // the hash so the token doesn't linger in the address bar or history.
    const hashMatch = window.location.hash.match(/(?:^#|&)imp=([^&]+)/);
    if (hashMatch) {
      sessionStorage.setItem(IMPERSONATION_KEY, decodeURIComponent(hashMatch[1]));
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }

    const token = getStoredToken();
    if (token) {
      try {
        const decoded = jwtDecode(token);
        const now = Date.now() / 1000;
        if (decoded.exp && decoded.exp < now) {
          sessionStorage.removeItem(IMPERSONATION_KEY);
          localStorage.removeItem(TOKEN_KEY);
          setUser(null);
        } else {
          setUser(decoded);
        }
      } catch {
        sessionStorage.removeItem(IMPERSONATION_KEY);
        localStorage.removeItem(TOKEN_KEY);
        setUser(null);
      }
    }
    setLoading(false);
  }, []);

  const login = useCallback((token) => {
    // A fresh, real login always wins over any leftover impersonation session
    // in this tab (e.g. this tab previously had "Log in as X" opened in it).
    sessionStorage.removeItem(IMPERSONATION_KEY);
    localStorage.setItem(TOKEN_KEY, token);
    try {
      const decoded = jwtDecode(token);
      setUser(decoded);
      return decoded;
    } catch {
      return null;
    }
  }, []);

  const logout = useCallback(() => {
    sessionStorage.removeItem(IMPERSONATION_KEY);
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  }, []);

  const getToken = useCallback(() => getStoredToken(), []);

  const isAuthenticated = !!user;

  return (
    <AuthContext.Provider value={{ user, login, logout, isAuthenticated, loading, getToken }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export default AuthContext;
